<template>
  <div class=" bg-transparent">
    <!-- Header Slot -->
    <div class="header-section mb-4">
      <slot name="header">
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <p class="mb-1 text-xs fw-bold">{{ title }}</p>
            <p class="text-muted mb-0 text-xs">{{ description }}</p>
          </div>
          <button 
            v-if="showCreateButton" 
            class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark" 
            @click="$emit('create')"
          >
            {{ createButtonText }}
          </button>
        </div>
      </slot>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-4">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p class="mt-2 text-muted">Loading {{ title.toLowerCase() }}...</p>
    </div>

    <!-- Empty State -->
    <div v-else-if="items.length === 0" class="text-center py-5">
      <slot name="empty-state">
        <i class=" bx bx-folder-open fs-3 text-muted mb-3"></i>
        <h5 class="text-muted">No {{ title.toLowerCase() }} found</h5>
        <p class="text-muted">{{ emptyStateMessage }}</p>
        <button 
          v-if="showCreateButton" 
          class="btn btn-primary" 
          @click="$emit('create')"
        >
          {{ createButtonText }}
        </button>
      </slot>
    </div>

    <!-- Folder List Structure -->
    <div v-else class="folder-list-container " >
      <div
        v-for="item in sortedItems"
        :key="item._key ?? (item.is_child ? `child-${item.id}-${item['Application No']}` : `app-${item['Application No']}`)"
        class="folder-item border-0"
      >
        <div class="folder-header d-flex justify-content-between align-items-center ">
          <div class="d-flex align-items-center gap-2">
            <i class="bx bx-folder-open fs-4 text-dark-blue ms-2 me-2 fs-4"></i>
            <div class="p-2">
              <!-- Dynamic fields based on columns configuration -->
              <p 
                v-for="column in mainDisplayColumns" 
                :key="column.key"
                class="mb-0 text-xs"
              >
                <span class="fw-bold mt-1">{{ column.label }} : </span>
                <slot 
                  :name="`column-${column.key}`" 
                  :item="item" 
                  :value="getNestedValue(item, column.key)"
                >
                  <!-- Default column rendering -->
                  <span v-if="column.formatter">
                    {{ column.formatter(getNestedValue(item, column.key), item) }}
                  </span>
                  <span v-else-if="column.type === 'date'">
                    {{ formatDate(getNestedValue(item, column.key)) }}
                  </span>
                  <span v-else-if="column.type === 'badge'">
                    <span class="badge" :class="column.badgeClass || 'bg-secondary'">
                      {{ getNestedValue(item, column.key) }}
                    </span>
                  </span>
                  <span v-else>
                    {{ getNestedValue(item, column.key) }}
                  </span>
                </slot>
              </p>
              
              <!-- Date field if specified -->
              <small 
                v-if="dateField" 
                class="text-muted text-xs"
              >
                {{ formatDate(getNestedValue(item, dateField)) }}
              </small>
            </div>
          </div>

          <div class="actions d-flex align-items-center">
            <button 
              v-if="actions.includes('view')"
              class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark me-2" 
              @click="onView(item)"
            >
              View
            </button>
            <button 
              v-if="actions.includes('add-sub')"
              class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark me-2" 
              @click="$emit('add-sub', item)"
            >
              Add Sub-Entity
            </button>
            <button 
              v-if="actions.includes('view_logs')"
              class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark me-2" 
              @click="$emit('view_logs', item)"
            >
              Logs
            </button>
            <button 
              v-if="actions.includes('edit')"
              class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark me-2" 
              @click="onEdit(item)"
            >
              Edit
            </button>
            <button 
              v-if="actions.includes('delete')"
              class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark me-2" 
              @click="onDelete(item)"
            >
              Unassign
            </button>

            <!-- Custom actions slot -->
            <slot name="custom-actions" :item="item"></slot>

            <!-- Expand / collapse (only if item has children) -->
            <button
              v-if="item.children && item.children.length"
              class="btn btn-sm btn-primary-dark text-xxs text-dark"
              @click="onToggle(item)"
            >
              <i :class="item.expanded ? 'fa-solid fa-angle-down' : 'fa-solid fa-angle-right'"></i>
            </button>
          </div>
        </div>

        <!-- Recursive Children -->
        <div v-if="item.children && item.children.length && item.expanded" class="children-container">
          <div class="hierarchy-line">
            <GenericAttributesList
              :items="item.children"
              :columns="columns"
              :actions="actions"
              :show-create-button="false"
              :date-field="dateField"
              :main-display-count="mainDisplayCount"
              @view="$emit('view', $event)"
              @edit="$emit('edit', $event)"
              @delete="$emit('delete', $event)"
              @add-sub="$emit('add-sub', $event)"
              @view_logs="$emit('view_logs', $event)"
              @toggle="$emit('toggle', $event)"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="pagination && items.length > 0" class="d-flex justify-content-between align-items-center mt-3">
      <div class="text-muted small">
        Showing {{ items.length }} items
      </div>
      <slot name="pagination"></slot>
    </div>
  </div>
</template>

<script>
export default {
  name: "GenericAttributesList",
  props: {
    // Data
    items: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    
    // Configuration
    title: {
      type: String,
      default: "Items"
    },
    description: {
      type: String,
      default: "Manage your items"
    },
    columns: {
      type: Array,
      default: () => [
        { key: 'question_text', label: 'Question Text' },
        { key: 'question_type', label: 'Type', type: 'badge' },
        { key: 'question_number', label: 'Number' },
        { key: 'date_created', label: 'Date Created', type: 'date' }
      ]
    },
    
    // Features
    showActions: {
      type: Boolean,
      default: true
    },
    actions: {
      type: Array,
      default: () => ['view', 'edit', 'delete']
    },
    showCreateButton: {
      type: Boolean,
      default: true
    },
    createButtonText: {
      type: String,
      default: "Add New"
    },
    emptyStateMessage: {
      type: String,
      default: "Get started by creating your first item"
    },
    pagination: {
      type: Boolean,
      default: false
    },
    sortBy: {
      type: String,
      default: 'question_number'
    },
    sortDirection: {
      type: String,
      default: 'asc',
      validator: (value) => ['asc', 'desc'].includes(value)
    },
    // New props for folder view
    dateField: {
      type: String,
      default: 'date_created'
    },
    mainDisplayCount: {
      type: Number,
      default: 2 // Number of main fields to show in the folder header
    }
  },
  computed: {
    sortedItems() {
      if (!this.sortBy) return this.items;
      
      return [...this.items].sort((a, b) => {
        const aValue = this.getNestedValue(a, this.sortBy);
        const bValue = this.getNestedValue(b, this.sortBy);
        
        if (this.sortDirection === 'asc') {
          return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
        } else {
          return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
        }
      });
    },
    
    mainDisplayColumns() {
      // Return first N columns for main display
      return this.columns.slice(0, this.mainDisplayCount);
    }
  },
  emits: [
    'create', 
    'edit', 
    'view', 
    'delete', 
    'add-sub',
    'view_logs',
    'toggle'
  ],
  methods: {
    getNestedValue(obj, path) {
      return path.split('.').reduce((current, key) => {
        return current && current[key] !== undefined ? current[key] : null;
      }, obj);
    },
    
    formatDate(dateString) {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },
    
    onEdit(item) {
      this.$emit('edit', item);
    },
    
    onView(item) {
      this.$emit('view', item);
    },
    
    onDelete(item) {
      this.$emit('delete', item);
    },
    
    onCreate() {
      this.$emit('create');
    },
    
    onToggle(item) {
      // Toggle expanded state
      this.$set(item, 'expanded', !item.expanded);
      this.$emit('toggle', item);
    }
  }
};
</script>

<style scoped>
.folder-item {
  border-radius: 8px;
  margin-bottom: 12px;
  background: white;
  transition: box-shadow 0.2s ease;
}

.folder-item:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.folder-header {
  padding: 12px;
  /* background: #f8f9fa; */
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
  border-radius: 8px 8px 0 0;
}

.children-container {
  margin-left: 32px;
  position: relative;
}

.hierarchy-line {
  position: relative;
  padding-left: 20px;
}

.hierarchy-line::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 2px;
  background: #dee2e6;
}

.actions .btn {
  margin-left: 4px;
}

/* Responsive design */
@media (max-width: 768px) {
  .folder-header {
    flex-direction: column;
    align-items: flex-start !important;
  }
  
  .actions {
    margin-top: 12px;
    width: 100%;
    justify-content: flex-end;
  }
  
  .children-container {
    margin-left: 16px;
  }
}
</style>